import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useT } from "../i18n";

// ---------------------------------------------------------------------------
// Cartonașul de explicație — UN SINGUR mecanism pentru tot proiectul
// ---------------------------------------------------------------------------
//
// Îl folosesc `Info` (mărimile scrise local), `Marime` (catalogul de mărimi),
// `Verificare` (catalogul de verificări) și `Verdict`. Înainte fiecare avea
// propria poziționare, propria lățime și propriul comportament la hover; la
// Pasul 19 explicația unei verificări are 1.164 px de conținut, iar cutia
// încăpea 425 — restul de 64 % era pur și simplu inaccesibil, fiindcă
// `.fx-tip` avea `pointer-events: none` și rotița mergea la pagina de
// dedesubt (măsurat pe env 2, proiectul 17).
//
// Ce face acum, în ordinea în care le întâlnește cineva care citește:
//
//   1. HOVER — se deschide ca înainte, fără clic.
//   2. MOUSE-UL INTRĂ ÎN CARTONAȘ — rămâne deschis. Între declanșator și
//      cartonaș e o pauză de 180 ms, cât să treci golul de 6 px fără să se
//      închidă.
//   3. DERULARE ÎN INTERIOR — corpul are `overflow-y: auto` ȘI primește
//      evenimentele de mouse. `overscroll-behavior: contain` oprește
//      „scroll chaining": când ajungi la capătul explicației, pagina de sub
//      ea NU începe să curgă.
//   4. CLIC PE DECLANȘATOR = FIXARE — cartonașul rămâne deschis până la Esc,
//      un clic în afara lui sau clic pe ✕. Abia fixat poți selecta textul,
//      deci se poate copia o formulă. Fixat, are chenar deschis la culoare.
//
// Regula casei: nicio pagină nu-și mai poziționează singură un tooltip. Dacă
// ai nevoie de altă lățime, dă `latime`; dacă ai nevoie de alt conținut, dă
// alt `children`. Comportamentul rămâne același peste tot, ca cititorul să
// nu-l reînvețe la fiecare pas.

type Poz = { left: number; top: number; maxH: number; sus: boolean };

const INTARZIERE_INCHIDERE_MS = 180;
// sub atâta loc într-o parte, cartonașul se mută în cealaltă chiar dacă
// preferința era „dedesubt"
const LOC_MINIM_PX = 260;

export default function HoverCard({
  children,
  continut,
  latime = 420,
  clasaAncora = "",
  clasaCard = "",
  eticheta,
}: {
  /** declanșatorul: iconița ⓘ, titlul verificării, verdictul ✓/✗ */
  children: ReactNode;
  /** explicația propriu-zisă */
  continut: ReactNode;
  latime?: number;
  clasaAncora?: string;
  clasaCard?: string;
  /** citit de screen-reader pe declanșator */
  eticheta?: string;
}) {
  const ancora = useRef<HTMLSpanElement>(null);
  const corp = useRef<HTMLSpanElement>(null);
  const ceas = useRef<number | null>(null);
  const [poz, setPoz] = useState<Poz | null>(null);
  const [fixat, setFixat] = useState(false);
  // «mai e text dedesubt» — indicatorul se arată doar când chiar e nevoie
  const [maiEste, setMaiEste] = useState(false);

  const opreste = () => {
    if (ceas.current != null) {
      window.clearTimeout(ceas.current);
      ceas.current = null;
    }
  };

  const aseaza = useCallback(() => {
    const a = ancora.current?.getBoundingClientRect();
    if (!a) return;
    const w = Math.min(latime, window.innerWidth - 24);
    let left = a.left + a.width / 2 - w / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - w - 12));
    const jos = window.innerHeight - a.bottom - 14;
    const sus = a.top - 14;
    // se așază pe partea cu mai mult loc; „dedesubt" câștigă la egalitate,
    // fiindcă acolo se uită ochiul după ce a citit declanșatorul
    const inSus = jos < LOC_MINIM_PX && sus > jos;
    setPoz({
      left,
      top: inSus ? a.top - 6 : a.bottom + 6,
      sus: inSus,
      maxH: Math.max(200, (inSus ? sus : jos) - 4),
    });
  }, [latime]);

  const arata = useCallback(() => {
    opreste();
    aseaza();
  }, [aseaza]);

  const ascunde = useCallback(() => {
    if (fixat) return;
    opreste();
    ceas.current = window.setTimeout(() => setPoz(null), INTARZIERE_INCHIDERE_MS);
  }, [fixat]);

  // Esc închide, oriunde ar fi focusul; un clic în afară desfixează
  useEffect(() => {
    if (!poz) return;
    const laTasta = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setFixat(false);
        setPoz(null);
      }
    };
    const laClic = (e: MouseEvent) => {
      if (!fixat) return;
      const t = e.target as Node;
      if (corp.current?.contains(t) || ancora.current?.contains(t)) return;
      setFixat(false);
      setPoz(null);
    };
    // repoziționare la derularea paginii: cartonașul e `position: fixed`, deci
    // altfel ar rămâne agățat în aer când pagina curge sub el
    const laDerulare = () => aseaza();
    window.addEventListener("keydown", laTasta);
    window.addEventListener("mousedown", laClic);
    window.addEventListener("scroll", laDerulare, true);
    window.addEventListener("resize", laDerulare);
    return () => {
      window.removeEventListener("keydown", laTasta);
      window.removeEventListener("mousedown", laClic);
      window.removeEventListener("scroll", laDerulare, true);
      window.removeEventListener("resize", laDerulare);
    };
  }, [poz, fixat, aseaza]);

  // «mai e text dedesubt»: se recalculează la deschidere și la derulare
  useLayoutEffect(() => {
    const el = corp.current?.querySelector(".hc-corp") as HTMLElement | null;
    if (!el) return;
    const masoara = () =>
      setMaiEste(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
    masoara();
    el.addEventListener("scroll", masoara);
    return () => el.removeEventListener("scroll", masoara);
  }, [poz]);

  const t = useT();

  useEffect(() => () => opreste(), []);

  return (
    <span
      className={`hc ${clasaAncora}`}
      onMouseEnter={arata}
      onMouseLeave={ascunde}
      onFocus={arata}
      onBlur={ascunde}
    >
      <span
        ref={ancora}
        className="hc-ancora"
        tabIndex={0}
        role="button"
        aria-label={eticheta}
        aria-expanded={poz != null}
        onClick={(e) => {
          e.stopPropagation();
          if (fixat) {
            setFixat(false);
            setPoz(null);
          } else {
            setFixat(true);
            arata();
          }
        }}
      >
        {children}
      </span>
      {poz && (
        <span
          ref={corp}
          className={`fx-tip hc-card ${fixat ? "hc-fixat" : ""} ${clasaCard}`}
          role="tooltip"
          style={{
            left: poz.left,
            top: poz.sus ? undefined : poz.top,
            bottom: poz.sus ? window.innerHeight - poz.top : undefined,
            width: Math.min(latime, window.innerWidth - 24),
            maxHeight: poz.maxH,
          }}
          onMouseEnter={arata}
          onMouseLeave={ascunde}
        >
          <span className="hc-corp" style={{ maxHeight: poz.maxH - 26 }}>
            {continut}
          </span>
          <span className="hc-subsol">
            {maiEste ? (
              <span className="hc-mai">{t("struct.hover.mai_jos")}</span>
            ) : (
              <span />
            )}
            {fixat ? (
              <button
                className="hc-inchide"
                onClick={() => {
                  setFixat(false);
                  setPoz(null);
                }}
              >
                {t("struct.hover.inchide")}
              </button>
            ) : (
              <span className="hc-mai">{t("struct.hover.fixeaza")}</span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}
